import appStore from '../utils/state';
import { ProductVariant, SizeSelectorButtonState } from '../types';
import { addToCart } from '../utils/addToCart';
import ButtonModular from './button-modular';

type CookiebotConsent = {
  marketing?: boolean;
};

type CookiebotGlobal = {
  consent?: CookiebotConsent;
};

function getCookiebot(): CookiebotGlobal | undefined {
  console.log('getCookiebot', (window as Window & { Cookiebot?: CookiebotGlobal }).Cookiebot);
  return (window as Window & { Cookiebot?: CookiebotGlobal }).Cookiebot;
}

export default class SizeSelectorForm extends HTMLElement {
  private submitButton: ButtonModular | null = null;
  private submitButtonElement: HTMLButtonElement | null = null;
  private form: HTMLFormElement | null = null;

  private mode: 'simple' | 'compound' = 'simple';
  private variants: ProductVariant[] = [];
  private lowInStockThreshold = 10;
  private lowStockLabelTemplate = '';
  private currentVariant: ProductVariant | null = null;
  private committedVariant: ProductVariant | null = null;

  get selectedVariant(): ProductVariant | null {
    return this.committedVariant;
  }

  // Publish a draft selection only when the customer dismisses the modal without adding.
  commitSelection(): void {
    this.publishSelection(this.currentVariant);
  }

  private publishSelection(variant: ProductVariant | null): void {
    this.committedVariant = variant;
    this.dispatchEvent(new CustomEvent('size-selector:change', { bubbles: true, detail: variant }));
  }

  private currentButtonState: SizeSelectorButtonState = 'select-size';
  private isGiftCard = false;

  private klaviyoVariantInput: HTMLInputElement | null = null;

  private onFormChangeBound = this.onFormChange.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);
  private onMarketingConsentChangeBound = this.onMarketingConsentChange.bind(this);

  connectedCallback(): void {
    this.mode = this.dataset.mode === 'compound' ? 'compound' : 'simple';
    this.isGiftCard = this.dataset.giftCard === 'true';

    this.form = this.querySelector('[data-size-selector-form-ref="form"]');
    this.submitButton = this.querySelector('[data-size-selector-form-ref="submit-button"]');
    this.submitButtonElement = this.submitButton?.querySelector('[data-button-modular-ref="button"]') ?? null;

    if (this.dataset.variants) {
      try {
        this.variants = JSON.parse(this.dataset.variants) as ProductVariant[];
      } catch {
        this.variants = [];
      }
    }

    this.lowInStockThreshold = Number(this.dataset.lowInStockThreshold) || 10;
    this.lowStockLabelTemplate = this.dataset.lowStockLabelTemplate ?? '';

    this.klaviyoVariantInput = document.querySelector('#klaviyo-bis-variant-id');

    this.addEventListeners();

    if (this.mode === 'compound') {
      this.updateBaseOptions();
      this.syncCompoundSelection();
    } else {
      customElements.whenDefined('button-modular').then(() => {
        this.submitButton = this.querySelector('[data-size-selector-form-ref="submit-button"]');
        this.submitButtonElement = this.submitButton?.querySelector('[data-button-modular-ref="button"]') ?? null;
        this.syncSimpleSelection();
      });
    }
  }

  addEventListeners(): void {
    if (this.form) {
      this.form.addEventListener('change', this.onFormChangeBound);
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }

    window.addEventListener('CookiebotOnConsentReady', this.onMarketingConsentChangeBound);
    window.addEventListener('CookiebotOnAccept', this.onMarketingConsentChangeBound);
    window.addEventListener('CookiebotOnDecline', this.onMarketingConsentChangeBound);
  }

  removeEventListeners(): void {
    if (this.form) {
      this.form.removeEventListener('change', this.onFormChangeBound);
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }

    window.removeEventListener('CookiebotOnConsentReady', this.onMarketingConsentChangeBound);
    window.removeEventListener('CookiebotOnAccept', this.onMarketingConsentChangeBound);
    window.removeEventListener('CookiebotOnDecline', this.onMarketingConsentChangeBound);
  }

  private onMarketingConsentChange(): void {
    if (this.mode === 'simple') {
      this.syncSimpleSelection();
      return;
    }

    this.syncCompoundSelection();
  }

  private hasMarketingConsent(): boolean {
    const cookiebot = getCookiebot();
    if (!cookiebot) {
      return true;
    }

    return cookiebot.consent?.marketing === true;
  }

  private applyOutOfStockButtonState(): void {
    const isQuickBuy = this.dataset.closeModal === 'quick-buy';

    if (!isQuickBuy && this.hasMarketingConsent()) {
      this.currentButtonState = 'notify-me';
      this.submitButton?.gotToStateIndex(2);
      return;
    }

    this.currentButtonState = 'unavailable';
    this.submitButton?.gotToStateIndex(3);
  }

  onFormChange(event: Event): void {
    if (!this.form) {
      return;
    }

    if (this.mode === 'simple') {
      this.syncSimpleSelection();
      return;
    }

    const target = event.target as HTMLInputElement;

    if (target.name === 'size_length') {
      this.updateBaseOptions();
    }

    this.syncCompoundSelection();
  }

  private syncSimpleSelection(): void {
    if (!this.form) {
      return;
    }

    const formData = new FormData(this.form);
    const variantId = formData.get('variant') as string;

    const variant = this.findVariantById(variantId);
    this.currentVariant = variant ?? null;
    if (variant && variant.available) {
      this.currentButtonState = 'add-to-cart';
      this.updateKlaviyoVariantInput(variant.id);

      if (this.submitButton) {
        this.submitButton.gotToStateIndex(1);
      }
    } else if (variant && !variant.available) {
      this.updateKlaviyoVariantInput(variant.id);
      this.applyOutOfStockButtonState();
    } else {
      this.currentButtonState = 'select-size';
      this.submitButton?.gotToStateIndex(0);
    }

    this.updateSubmitButtonAria();
  }

  private getSelectedLength(): string | null {
    if (!this.form) {
      return null;
    }

    return this.form.querySelector<HTMLInputElement>('input[name="size_length"]:checked')?.value ?? null;
  }

  private getSelectedBase(): string | null {
    if (!this.form) {
      return null;
    }

    return this.form.querySelector<HTMLInputElement>('input[name="size_base"]:checked')?.value ?? null;
  }

  private findVariantById(id: string): ProductVariant | undefined {
    return this.variants.find((variant) => variant.id.toString() === id);
  }

  private findVariantByBaseAndLength(base: string, length: string): ProductVariant | undefined {
    return this.variants.find((variant) => variant.base === base && variant.length === length);
  }

  private updateBaseOptions(): void {
    const length = this.getSelectedLength();

    if (!length || !this.form) {
      return;
    }

    const baseInputs = this.form.querySelectorAll<HTMLInputElement>('input[name="size_base"]');

    baseInputs.forEach((input) => {
      const variant = this.findVariantByBaseAndLength(input.value, length);
      const label = input.closest('label');

      if (!label) {
        return;
      }

      const isUnavailable = !variant?.available;
      const stockValue = variant?.inventory_quantity ?? 0;
      const isLowInStock = !isUnavailable && stockValue > 0 && stockValue < this.lowInStockThreshold;

      const labelText = label.querySelector('[data-checkbox-radio-ref="label-text"]');
      const unavailableIndicator = label.querySelector('[data-checkbox-radio-ref="unavailable-indicator"]');
      const lowStockIndicator = label.querySelector('[data-checkbox-radio-ref="low-stock-indicator"]');

      if (labelText) {
        labelText.classList.toggle('text-charcoal', !isUnavailable);
        labelText.classList.toggle('text-charcoal-60-opacity', isUnavailable);
        labelText.classList.toggle('line-through', isUnavailable);
      }

      unavailableIndicator?.classList.toggle('hidden', !isUnavailable);
      lowStockIndicator?.classList.toggle('hidden', !isLowInStock);

      if (lowStockIndicator && isLowInStock) {
        lowStockIndicator.textContent = this.lowStockLabelTemplate.replace('[count]', stockValue.toString());
      }
    });
  }

  private syncCompoundSelection(): void {
    const length = this.getSelectedLength();
    const base = this.getSelectedBase();

    if (!length || !base) {
      this.currentVariant = null;
      this.currentButtonState = 'select-size';

      if (this.submitButton) {
        this.submitButton.gotToStateIndex(0);
      }

      this.updateSubmitButtonAria();
      return;
    }

    const variant = this.findVariantByBaseAndLength(base, length);
    this.currentVariant = variant ?? null;
    if (variant) {
      this.updateKlaviyoVariantInput(variant.id);

      if (variant?.available) {
        this.currentButtonState = 'add-to-cart';

        if (this.submitButton) {
          this.submitButton.gotToStateIndex(1);
        }
      } else {
        this.applyOutOfStockButtonState();
      }
    } else {
      this.currentButtonState = 'select-size';
      this.submitButton?.gotToStateIndex(0);
    }

    this.updateSubmitButtonAria();
  }

  private updateSubmitButtonAria(): void {
    if (!this.submitButtonElement) {
      return;
    }

    if (this.currentButtonState === 'notify-me') {
      this.submitButtonElement.setAttribute('aria-haspopup', 'dialog');
      this.submitButtonElement.setAttribute('aria-controls', 'out-of-stock-modal-dialog');
    } else {
      this.submitButtonElement.removeAttribute('aria-haspopup');
      this.submitButtonElement.removeAttribute('aria-controls');
    }
  }

  onFormSubmit(event: Event, variant: ProductVariant | null = this.currentVariant): void {
    event.preventDefault();

    if (variant !== null && variant.available) {
      const committedAtSubmission = this.committedVariant;
      const addition = addToCart({
        id: variant.id.toString(),
        quantity: 1,
        gift_card: this.isGiftCard,
        ...(this.dataset.offerFreeGift === 'true' ? { properties: { _offer_free_gift: '1' } } : {}),
      });

      if (this.dataset.closeModal === 'quick-buy') {
        appStore.actions.closeQuickBuyModal();
      } else {
        appStore.actions.closeSizeSelectorModal();
      }

      if (this.closest('.main-product[data-pdp-version="v3"]')) {
        void addition.then((cart) => {
          if (!cart) return;

          // A delayed response must not discard a newer draft or committed selection.
          if (this.currentVariant === variant) {
            this.form?.reset();
            if (this.mode === 'compound') {
              this.updateBaseOptions();
              this.syncCompoundSelection();
            } else {
              this.syncSimpleSelection();
            }
          }
          if (this.committedVariant === committedAtSubmission) {
            this.publishSelection(null);
          }
        });
      }
    } else if (
      this.currentVariant !== null &&
      !this.currentVariant.available &&
      this.currentButtonState === 'notify-me'
    ) {
      this.triggerKlaviyoBIS();
    }
  }

  private updateKlaviyoVariantInput(variantId: number): void {
    if (this.klaviyoVariantInput) {
      this.klaviyoVariantInput.value = variantId.toString();
    }
  }

  private triggerKlaviyoBIS(): void {
    const klaviyoBtn = document.querySelector<HTMLElement>('.klaviyo-bis-injected-btn');
    if (klaviyoBtn) {
      klaviyoBtn.click();
      if (this.currentVariant) {
        appStore.actions.closeSizeSelectorModal();
      }
    }

    // if (this.currentVariant) {
    //   appStore.actions.openOutOfStockModal(this.currentVariant);
    // }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }
}

customElements.define('size-selector-form', SizeSelectorForm);
